import React, { ReactElement, useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import PageTitleSubheader from '../../layout/subheader/PageTitleSubheader';
import { OutletContext } from '../../layout/Layout';
import { AlertVariant, Modal, ModalVariant } from '@patternfly/react-core';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib/components/toast/ToastProvider';
import ZiTable, { Action, Column } from '../../components/table/ZiTable';
import FilterTableLayout from '../../layout/FilterTableLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons/faPenToSquare';
import SchnurForm, { EventType, Field, UIType } from '../../components/form/SchnurForm/SchnurForm';
import { Permission } from '../../enums/permission.enum';
import { TZoneSetting, ZoneSetting, ZoneSettingEnum } from '../../api/zone/ZoneSetting';
import FilterIcon from '@fortawesome/fontawesome-pro/svgs/regular/filter.svg';
import { getEntityMetaInfoTitle } from '../../helpers/helper-components/EntityMetadataHelper';

export default function ZoneSettings(): ReactElement {
	const { addToast } = useToast();
	const { setSubSide, subNavExpanded, setSubNavExpanded } = useOutletContext<OutletContext>();
	const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
	const [data, setData] = useState<ZoneSetting[]>([]);
	const [tableLoading, setTableLoading] = useState<boolean>(true);
	const [activeSetting, setActiveSetting] = useState<TZoneSetting>(
		ZoneSetting.DefaultNewZoneSetting() as TZoneSetting
	);
	const [isFormLoading, setIsFormLoading] = useState<boolean>(false);
	const navigate = useNavigate();

	const selectedColumns: Column<TZoneSetting>[] = [
		{
			title: 'Name',
			columnName: 'name',
			sortable: true,
		},
		{
			title: 'Description',
			columnName: 'description',
			sortable: true,
		},
		{
			title: 'Enabled',
			columnName: 'enabled',
			sortable: true,
			customAccessor: (item) => String(item.enabled ? 'Yes' : 'No'),
		},
	];

	const formProperties: Field<TZoneSetting>[] = [
		{
			title: 'Name',
			columnName: 'name',
			uiSchema: {
				type: UIType.TEXT,
			},
			disabled: true,
		},
		{
			title: 'Description',
			columnName: 'description',
			uiSchema: {
				type: UIType.TEXTAREA,
			},
			maxLength: 255,
		},
		{
			title: 'Value',
			columnName: 'value',
			uiSchema: {
				type: UIType.TEXTAREA,
			},
			required: true,
			validate: (eventType, value) => {
				if (eventType !== EventType.BLUR && eventType !== EventType.CHANGE) {
					return true;
				}

				return true;
			},
		},
		{
			title: 'Enabled',
			columnName: 'enabled',
			uiSchema: {
				type: UIType.BOOLEAN,
			},
		},
	];

	const actions: Action<ZoneSetting>[] = [
		{
			name: (
				<>
					<FontAwesomeIcon icon={faPenToSquare} />
					Edit
				</>
			),
			callback: (item) => {
				if (
					item.description ==
					'Short List of quick filter Tags to display on Discover page'
				) {
					item.description =
						'Short List of quick filter Tags to display on Discover page \n(Case Insensitive, comma separated string of existing tags) \nFor tags that have spaces in them enter as a hypen instead, ie. "pay equity" should be entered as "pay-equity"';
				}
				setActiveSetting(item);
				setIsModalOpen(true);
			},
			permission: Permission.CanEditZoneSettings,
		},
	];

	useEffect(() => {
		setSubSide({
			subheaderContext: (
				<PageTitleSubheader
					pageTitle="Zone Settings"
					pageDescription="Manage settings for all zones."
					expanded={subNavExpanded}
					setExpanded={setSubNavExpanded}
				/>
			),
		});

		ZoneSetting.GetAll()
			.then((setting) => {
				//dont show the zone color palette setting!
				setData(setting.filter((x) => x.zone_setting_enum_id != 25));
				setTableLoading(false);
			})
			.catch(() => {
				addToast(
					'An error occurred while trying to load Zone Settings. Please try again later.',
					AlertVariant.danger
				);
			});
	}, [setSubSide, subNavExpanded, setSubNavExpanded]);

	useEffect(() => {
		if (isModalOpen) {
			document.querySelectorAll('.pf-c-form-control').forEach((el, idx) => {
				if (el.tagName === 'TEXTAREA') {
					const elValue = el.innerHTML;

					if (elValue.includes('"window_title"')) {
						(el as HTMLTextAreaElement).style.height = '250px';
					}

					if (elValue.includes('Short List of quick filter')) {
						(el as HTMLTextAreaElement).style.height = '110px';
					}
				}
			});
		}
	}, [isModalOpen]);

	const handleClose = () => {
		setIsModalOpen(false);
	};

	const handleSuccess = (value: ZoneSetting) => {
		const index = data.findIndex((setting) => setting.id === value.id);

		if (index >= 0) {
			setData((prev) => [...prev.slice(0, index), value, ...prev.slice(index + 1)]);
		} else {
			setData((prev) => [...prev, value]);
		}

		const isAppBrandingZoneSetting =
			value.zone_setting_enum_id == ZoneSettingEnum.applicationWindowTitle;
		const zoneSettingEnabled = value.enabled;

		if (isAppBrandingZoneSetting) {
			setTableLoading(true);
			addToast(
				'Application Branding setting has been updated. Reloading to apply changes...',
				AlertVariant.info
			);
			setTimeout(() => {
				localStorage.removeItem('AppBranding_BackgroundHeaderColor');
				localStorage.removeItem('AppBranding_BackgroundMenuColor');
				localStorage.removeItem('AppBranding_ApplicationTitle');
				localStorage.removeItem('AppBranding_BackgroundCanvasColor');
				localStorage.removeItem('AppBranding_ButtonColor');
				localStorage.removeItem('AppBranding_ButtonHoverColor');

				if (!zoneSettingEnabled) {
					setSubSide({
						subheaderContext: (
							<PageTitleSubheader
								pageTitle="Zone Settings"
								pageDescription="Manage settings for all zones."
								expanded={subNavExpanded}
								setExpanded={setSubNavExpanded}
							/>
						),
					});
					window.location.reload();
				} else {
					navigate('/analyze/loading');
					setTimeout(() => {
						navigate('/setup/settings/zone-settings');
					}, 3000);
				}
			}, 1000);
		}

		setIsModalOpen(false);
	};

	const zoneSettingsTable = (
		<ZiTable<ZoneSetting>
			ariaLabel={'Zone Settings table'}
			columns={selectedColumns}
			data={data}
			caption="Zone Settings"
			actions={actions}
			loading={tableLoading}
		/>
	);

	return (
		<React.Fragment>
			<Modal
				variant={ModalVariant.medium}
				isOpen={isModalOpen}
				onClose={handleClose}
				title="override"
				className="custom-title-modal"
			>
				<h1
					id="pf-modal-part-10"
					className="pf-c-modal-box__title"
				>
					<span className="pf-c-modal-box__title-text">
						{getEntityMetaInfoTitle('Zone Setting Management', activeSetting)}
					</span>
				</h1>
				<br />
				<SchnurForm<TZoneSetting>
					title={'Zone Setting Management'}
					fields={formProperties}
					initialSubject={activeSetting}
					isLoading={isFormLoading}
					onSubmit={(setting) => {
						setIsFormLoading(true);

						ZoneSetting.Update(setting)
							.then((updated) => {
								handleSuccess(updated);
							})
							.catch(() => {
								addToast(
									'An error occurred while trying to save Zone Settings. Please try again later.',
									AlertVariant.danger
								);
							})
							.finally(() => {
								setIsFormLoading(false);
							});
					}}
				/>
			</Modal>
			<React.Fragment>
				<FilterTableLayout
					table={zoneSettingsTable}
					icon={FilterIcon}
				/>
			</React.Fragment>
		</React.Fragment>
	);
}
